import {
  Button,
  Combobox,
  Divider,
  HelpText,
  Label,
  TextField,
} from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import { NativeField } from '@/components';
import type { FieldConfig } from '@/pages/print/schemas';
import { isDate, isTime } from '@/utils/print';
import { parseValue } from './hooks';

const DEFAULT_TIME = '00:00';

interface VariableSetRowProps {
  id: string;
  index: number;
  showIndex: boolean;
  variable: Record<string, string | number>;
  visibleFields: FieldConfig[];
  readonlyFields: Set<string>;
  disabledFields: Set<string>;
  variableDisplayNameMap: Map<string, string>;
  validationErrors: Record<string, string>;
  quantity: number;
  showRemove: boolean;
  onRemove: (id: string) => void;
  onChange: (id: string, name: string, value: string | number) => void;
  onQuantityChange: (id: string, value: number) => void;
}

export const VariableSetRow = ({
  id,
  index,
  showIndex,
  variable,
  visibleFields,
  readonlyFields,
  disabledFields,
  variableDisplayNameMap,
  validationErrors,
  quantity,
  showRemove,
  onRemove,
  onChange,
  onQuantityChange,
}: VariableSetRowProps) => {
  const { t } = useTranslation();

  return (
    <div>
      {showIndex && <Divider direction="horizontal" variant="strong" />}
      <div className={showIndex ? 'pt-400' : ''}>
        {(showIndex || showRemove) && (
          <div className="mb-300 flex items-center justify-between gap-200">
            {showIndex && (
              <span className="text-body-md font-bold text-core-slate-1300">
                {t('formPrintPage.variableSet', { number: index + 1 })}
              </span>
            )}
            {showRemove && (
              <Button
                variant="negative"
                appearance="ghost"
                size="small"
                iconName="trash"
                iconPosition="start"
                onClick={() => onRemove(id)}
                aria-label={t('formPrintPage.removeVariableSet')}
              >
                {t('formPrintPage.removeVariableSet')}
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-200">
          {/* Variable fields */}
          {visibleFields.length > 0 && (
            <div
              className="grid gap-x-200 gap-y-300"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
            >
              {visibleFields.map((fieldConfig) => {
                const { name, type, required, pickListValues } = fieldConfig;
                const displayName =
                  variableDisplayNameMap.get(name) ?? name.charAt(0).toUpperCase() + name.slice(1);
                const isRo = readonlyFields.has(name);
                const isDis = disabledFields.has(name);
                const value = variable[name] ?? fieldConfig.initialValue ?? '';
                const error = validationErrors[`${name}_${id}`];

                const handleChange = (raw: string) =>
                  onChange(id, name, parseValue(type, isTime(type) && !raw ? DEFAULT_TIME : raw));

                const label = (
                  <>
                    {displayName}
                    {required && <span className="text-core-red-1000"> *</span>}
                  </>
                );

                if (Array.isArray(pickListValues) && pickListValues.length > 0) {
                  return (
                    <div key={name} className="flex min-w-0 flex-col">
                      <Label>{label}</Label>
                      <Combobox
                        fullWidth
                        value={value ? [value as string] : []}
                        onChange={(_e, items) => onChange(id, name, items[0]?.value ?? '')}
                        placeholder={displayName}
                        disabled={isDis || isRo}
                        size="medium"
                        invalid={!!error}
                      >
                        {pickListValues.map((val: string) => (
                          <Combobox.Option key={val} value={val}>
                            {val}
                          </Combobox.Option>
                        ))}
                      </Combobox>
                      {error && <HelpText variant="negative">{error}</HelpText>}
                    </div>
                  );
                }

                // Connect TextField doesn't support date/time/number types
                const inputType =
                  type === 'Double'
                    ? 'number'
                    : isDate(type)
                      ? 'date'
                      : isTime(type)
                        ? 'time'
                        : 'text';

                if (inputType !== 'text') {
                  return (
                    <div key={name} className="flex min-w-0 flex-col">
                      <Label>{label}</Label>
                      <NativeField
                        type={inputType}
                        size="medium"
                        value={value as string}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={(e) => handleChange(e.target.value)}
                        readOnly={isRo}
                        disabled={isDis}
                        required={required}
                        aria-label={displayName}
                        validationState={error ? 'invalid' : undefined}
                      />
                      {error && (
                        <HelpText variant="negative" size="small">
                          {error}
                        </HelpText>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={name} className="flex min-w-0 flex-col">
                    <Label>{label}</Label>
                    <TextField
                      size="medium"
                      value={value as string}
                      onChange={(_e, v) => handleChange(v)}
                      readOnly={isRo}
                      disabled={isDis}
                      required={required}
                      validationState={error ? 'invalid' : undefined}
                      aria-label={displayName}
                    />
                    {error && (
                      <HelpText variant="negative" size="small">
                        {error}
                      </HelpText>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quantity — visually distinct from variable fields */}
          {visibleFields.length > 0 && <Divider direction="horizontal" variant="subtle" />}
          <div>
            <span className="text-body-xs font-medium text-core-slate-900">
              {t('printJobsPage.display.quantity', 'Quantity')}
            </span>
            <div className="mt-100 w-[80px]">
              <NativeField
                type="number"
                min={1}
                step={1}
                validationState={quantity < 1 ? 'invalid' : undefined}
                aria-label={t('printJobsPage.display.quantity', 'Quantity')}
                value={String(quantity)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onQuantityChange(id, Number.isFinite(v) ? v : 0);
                }}
              />
            </div>
            {quantity < 1 && (
              <HelpText variant="negative" size="small" className="whitespace-nowrap">
                {t('formPrintPage.quantityMinError', 'Quantity must be at least 1')}
              </HelpText>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
